/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { toNumber } from 'lodash'

import { AjaxRequestError } from '@modules/ui-types'
import type { Url } from '@modules/ui-types'
import { del, get, patch, post, put } from '@modules/utils/ajax'
import type { AjaxConfig, AjaxResult } from '@modules/utils/ajax'
import { captureApmError, startHttpTransaction } from '@modules/utils/apm'

import requiredParam from '../../lib/requiredParam'

import type { Method } from '.'

const methods = { get, post, put, delete: del, patch }

interface StandaloneAsyncRequestParams {
  method?: Method
  url: Url
  payload?: unknown | string
  requestSettings?: AjaxConfig['settings']
  responseMapper?: (payload: AjaxResult) => unknown
  includeHeaders?: boolean
  resetApmTransaction?: boolean
  maxResponseSize?: number
  maxCompressedResponseSize?: number
}

interface StandaloneAsyncRequestResult<TPayload = unknown> {
  data: TPayload | null
  headers?: { [key: string]: string }
  status: number
  error?: Error | string
}

class StandaloneAsyncRequestError extends Error {
  public data: null

  public status: number

  public headers?: { [key: string]: string }

  public originalError: unknown

  constructor(
    message: string,
    status: number,
    originalError: unknown,
    headers?: { [key: string]: string },
  ) {
    super(message)
    this.name = 'StandaloneAsyncRequestError'
    this.data = null
    this.status = status
    this.headers = headers
    this.originalError = originalError
  }
}

/**
 * Standalone async request function that doesn't use Redux dispatch
 * Returns a Promise with the response data and metadata
 */
export default async function standaloneAsyncRequest<TPayload = unknown>({
  method = 'get',
  url = requiredParam('url'),
  payload,
  requestSettings = {},
  responseMapper = defaultResponseMapper,
  includeHeaders = false,
  resetApmTransaction = true,
  maxResponseSize,
  maxCompressedResponseSize,
}: StandaloneAsyncRequestParams): Promise<StandaloneAsyncRequestResult<TPayload>> {
  const lowerMethod = method.toLowerCase()
  const isGet = lowerMethod === 'get'
  const request = methods[lowerMethod]

  const options: AjaxConfig = {
    settings: {
      ...requestSettings,
      response: {
        ...requestSettings.response,
        fullStatus: true,
      },
      maxResponseSize,
      maxCompressedResponseSize,
    },
    includeHeaders: true,
  }

  const params = isGet ? [url, options] : [url, payload, options]
  const httpTransaction = startHttpTransaction(method, url)

  try {
    const response = await request(...params)
    const { headers, status } = response
    const mappedData = responseMapper(response) as TPayload

    if (resetApmTransaction && httpTransaction) {
      httpTransaction.addLabels({ status_code: toNumber(status) })
      httpTransaction.end()
    }

    const result: StandaloneAsyncRequestResult<TPayload> = {
      data: mappedData,
      status: toNumber(status),
    }

    if (includeHeaders) {
      result.headers = headers
    }

    return result
  } catch (error) {
    if (error instanceof AjaxRequestError) {
      captureApmError(
        new Error(
          `${lowerMethod} failed: [${error.message}] [${error.response.status}] [${error.response.statusText}]`,
        ),
      )

      httpTransaction?.addLabels({ status_code: toNumber(error.response.status) })
      httpTransaction?.end()

      throw new StandaloneAsyncRequestError(
        `${lowerMethod} failed: [${error.message}]`,
        toNumber(error.response.status) || 0,
        error,
        includeHeaders
          ? (error.response.headers as unknown as { [key: string]: string })
          : undefined,
      )
    } else {
      const errorMessage = error instanceof Error ? error.message : String(error)
      captureApmError(new Error(`${lowerMethod} failed: [${errorMessage}]`))

      httpTransaction?.end()

      throw new StandaloneAsyncRequestError(`${lowerMethod} failed: [${errorMessage}]`, 0, error)
    }
  }
}

function defaultResponseMapper<TPayload>(response: AjaxResult): AjaxResult | TPayload {
  if (response.blob) {
    // blob consumers expect to be able to look at `blob` and `blobUrl` fields
    return response
  }

  return response.body
}
